import * as z from 'zod/mini';
import { lengthUnitSchema, type TestResult } from '../cable.ts';
import { READING_NOTES_MAX_LENGTH, READING_TESTER_MAX_LENGTH } from '../constants.ts';
import { dateSchema, idSchema, timestampSchema } from './common.ts';

// A run's tester readings (ROADMAP I9): one row per test of a run, kept in
// full — the wiremap verdict, the measured length, and the certification
// figures a tester reports (the worst-pair NEXT margin and the insertion loss,
// in dB) — with the tester and the day. A run keeps many over its life (a
// first pull, a re-terminate, a re-test), and the latest reading rolls up into
// the run's own `testResult`/`testedOn`/`measuredLength` the dashboard reads.

/** A reading's verdict; unlike a run's, a recorded reading always has one. */
export const READING_RESULTS = ['pass', 'fail', 'marginal'] as const;
export type ReadingResult = (typeof READING_RESULTS)[number];
export const readingResultSchema = z.enum(READING_RESULTS);

export const READING_RESULT_LABELS: Readonly<Record<ReadingResult, string>> = {
  pass: 'Pass',
  fail: 'Fail',
  marginal: 'Marginal',
};

/** The wiremap (pin-to-pin) verdict; '' when the test was not a copper wiremap. */
export const WIREMAP_RESULTS = ['', 'pass', 'fail'] as const;
export type WiremapResult = (typeof WIREMAP_RESULTS)[number];
export const wiremapResultSchema = z.enum(WIREMAP_RESULTS);

export const WIREMAP_RESULT_LABELS: Readonly<Record<WiremapResult, string>> = {
  '': 'Not recorded',
  pass: 'Pass',
  fail: 'Fail',
};

const readingFields = {
  testedOn: z.nullable(dateSchema),
  result: readingResultSchema,
  wiremap: wiremapResultSchema,
  lengthValue: z.nullable(z.number().check(z.nonnegative())),
  lengthUnit: lengthUnitSchema,
  /** The worst-pair NEXT margin in dB; a positive margin is headroom. */
  nextDb: z.nullable(z.number()),
  /** The insertion loss (attenuation) in dB. */
  attenuationDb: z.nullable(z.number()),
  tester: z.string().check(z.trim(), z.maxLength(READING_TESTER_MAX_LENGTH)),
  notes: z.string().check(z.trim(), z.maxLength(READING_NOTES_MAX_LENGTH)),
};

export const cableReadingSchema = z.object({
  id: idSchema,
  dropId: idSchema,
  ...readingFields,
  createdAt: timestampSchema,
});
export type CableReading = z.infer<typeof cableReadingSchema>;

export const cableReadingInputSchema = z.object({
  ...readingFields,
  testedOn: z._default(readingFields.testedOn, null),
  wiremap: z._default(readingFields.wiremap, ''),
  lengthValue: z._default(readingFields.lengthValue, null),
  lengthUnit: z._default(readingFields.lengthUnit, 'ft'),
  nextDb: z._default(readingFields.nextDb, null),
  attenuationDb: z._default(readingFields.attenuationDb, null),
  tester: z._default(readingFields.tester, ''),
  notes: z._default(readingFields.notes, ''),
});
export type CableReadingInput = z.infer<typeof cableReadingInputSchema>;

/** Whether a reading's verdict should be flagged on the run (failed, or among limits). */
export function readingNeedsAttention(result: ReadingResult): boolean {
  return result === 'fail' || result === 'marginal';
}

/**
 * The most recent reading of a run: the latest tested day, and the newest
 * created among readings that share a day (or have no day). Null when the run
 * has no readings.
 */
export function latestReading(readings: readonly CableReading[]): CableReading | null {
  let latest: CableReading | null = null;
  for (const reading of readings) {
    const rank = `${reading.testedOn ?? ''} ${reading.createdAt}`;
    const best = latest === null ? '' : `${latest.testedOn ?? ''} ${latest.createdAt}`;
    if (latest === null || rank >= best) {
      latest = reading;
    }
  }
  return latest;
}

/** The run summary a set of readings rolls up to: the latest reading's facts. */
export interface ReadingRollup {
  readonly testResult: TestResult;
  readonly testedOn: string | null;
  readonly measuredLength: number | null;
}

/**
 * What the latest reading says the run's rolled-up test is (ROADMAP I9), or
 * null when there is no reading to roll up (the run keeps whatever was set by
 * hand). A reading's `result` maps straight onto the run's `testResult`.
 */
export function readingRollup(readings: readonly CableReading[]): ReadingRollup | null {
  const latest = latestReading(readings);
  if (latest === null) {
    return null;
  }
  return {
    testResult: latest.result,
    testedOn: latest.testedOn,
    measuredLength: latest.lengthValue,
  };
}

/** "NEXT 4.2 dB", "loss 1.1 dB": the certification figures a reading recorded, joined; '' when none. */
export function describeReadingFigures(reading: CableReading): string {
  return [
    reading.wiremap === '' ? '' : `wiremap ${WIREMAP_RESULT_LABELS[reading.wiremap].toLowerCase()}`,
    reading.nextDb === null ? '' : `NEXT ${String(reading.nextDb)} dB`,
    reading.attenuationDb === null ? '' : `loss ${String(reading.attenuationDb)} dB`,
  ]
    .filter((part) => part !== '')
    .join(' · ');
}
